import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Shield, X } from 'lucide-react'
import useConsentStore from '@/stores/useConsentStore'
import useAuthStore from '@/stores/useAuthStore'
import { cn } from '@/utils/shadcn'

/**
 * Consent Banner Component (FR-48).
 * Shows a banner to logged-in users who haven't been asked about analytics consent yet.
 * If user declined, re-prompts after 30 days.
 * Appears at the bottom of the screen.
 */
const ConsentBanner = () => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { analyticsConsent, giveConsent, withdrawConsent, checkAndResetIfExpired } = useConsentStore()
  const [isVisible, setIsVisible] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    // Only show banner if user is logged in
    if (!user) {
      setIsVisible(false)
      return
    }

    // Check if 30 days have passed since last decline
    checkAndResetIfExpired()
    
    // Show banner only if user hasn't been asked yet (consent === null)
    if (analyticsConsent === null) {
      // Small delay to avoid showing immediately on page load
      const timer = setTimeout(() => setIsVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [analyticsConsent, checkAndResetIfExpired, user])

  const handleAccept = () => {
    giveConsent()
    closeBanner()
  }

  const handleDecline = () => {
    withdrawConsent()
    closeBanner()
  }

  const closeBanner = () => {
    setIsClosing(true)
    setTimeout(() => setIsVisible(false), 300)
  }

  if (!isVisible) return null

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ease-in-out',
        isClosing ? 'translate-y-full' : 'translate-y-0'
      )}
    >
      <div className="mx-auto max-w-screen-xl p-4">
        <div className="rounded-2xl border border-secondary-casper/20 bg-white p-6 shadow-2xl md:flex md:items-center md:justify-between md:gap-6">
          <div className="flex items-start gap-4 flex-1">
            <Shield className="h-6 w-6 text-brand-torea flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-medium text-brand-torea mb-2">
                {t('consent.banner_title', 'Help Us Improve Your Experience')}
              </h3>
              <p className="text-sm text-deep-cove leading-relaxed">
                {t(
                  'consent.banner_description',
                  'We use anonymous analytics to improve the app. No personal information is collected. You can change this anytime in your profile settings.'
                )}
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-3 md:mt-0 md:flex-shrink-0">
            <button
              onClick={handleDecline}
              className="flex-1 rounded-lg border border-secondary-casper/30 px-4 py-2 text-sm font-medium text-deep-cove transition hover:border-brand-torea hover:text-brand-torea md:flex-none"
            >
              {t('consent.decline', 'Decline')}
            </button>
            <button
              onClick={handleAccept}
              className="flex-1 rounded-lg bg-brand-torea px-6 py-2 text-sm font-medium text-white transition hover:bg-brand-torea/90 md:flex-none"
            >
              {t('consent.accept', 'Accept')}
            </button>
          </div>

          <button
            onClick={closeBanner}
            className="absolute top-4 right-4 rounded-full p-1 text-deep-cove transition hover:bg-secondary-casper/10 md:hidden"
            aria-label={t('consent.close', 'Close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConsentBanner
